import React, { useEffect } from 'react';
import styled from 'styled-components';
import { assets } from '../../assets';
import Button from '../../components/buttons/button';
import { MattRow } from '../../components/containers/containers';
import CenteredSpinner from '../../components/loading-indicators/centered-spinner';
import { HorizontalSeparatorGray } from '../../components/ui-utils/separators';
import { formatDateTime } from '../../helpers/date-formatters';
import { MessageListItem, Query } from '../../logic/graphql/types/types';
import { useQuery, useMutation } from 'urql';
import { GraphqlOperations } from '../../logic/graphql/operations';
import { useToasts } from 'react-toast-notifications';
import config from '../../../config';
import { FormattedMessage, useIntl } from 'react-intl';

const MessageDetailWrapper = styled(MattRow)`
  padding: 50px 50px;
  width: 100%;
  height: 100%;
  position: relative;
  @media (max-width: 1280px) {
    padding: 20px 20px;
    height: unset;
  }
`;

const ClockIcon = styled.img`
  width: 12px;
  height: 12px;
  margin-right: 4px;
`;

const AttachmentIcon = styled.img`
  width: 15px;
  height: 18px;
  margin-right: 8px;
`;

const AttachmentContainer = styled.section`
  padding: 20px 30px;
  background: var(--matt-lighter-bg-color);
  border-radius: 2px;
`;

const PdfIcon = styled.img`
  width: 14px;
  height: 18px;
  margin-right: 8px;
`;

const MessageBody = styled.p`
  white-space: pre-wrap;
`;

const MessageDetails = (props: { message: MessageListItem }): JSX.Element => {
  const { addToast } = useToasts();
  const acceptMessage = useMutation(GraphqlOperations.mutations.accept_message.mutationString)[1];
  const intl = useIntl();

  const [result, refetchMessage] = useQuery<Query>({
    query: GraphqlOperations.queries.get_message.queryString,
    variables: GraphqlOperations.queries.get_message.createVars(props.message.id),
  });
  const { data, fetching, error } = result;
  const message = data?.get_message;

  useEffect(() => {
    if (error) {
      addToast(intl.formatMessage({ id: 'messages.details.toast.error' }), {
        appearance: 'error',
        autoDismiss: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (fetching || !message) {
    return (
      <MessageDetailWrapper>
        <CenteredSpinner />
      </MessageDetailWrapper>
    );
  }
  const handleAcceptClick = (): void => {
    const variables = GraphqlOperations.mutations.accept_message.createVars(props.message.id);
    acceptMessage(variables)
      .then((result) => {
        if (result && !result.error) {
          refetchMessage({ requestPolicy: 'network-only' });
          addToast(intl.formatMessage({ id: 'messages.details.toast.acceptSuccess' }), {
            appearance: 'success',
            autoDismiss: true,
          });
        } else {
          addToast(intl.formatMessage({ id: 'messages.details.toast.acceptError' }), {
            appearance: 'error',
            autoDismiss: true,
          });
        }
        return true;
      })
      .catch((err) => console.log(err));
  };
  const openAttahcment = (fileName: string): void => {
    window.open(config.backend_url + '/attachments/' + message.id + '/' + fileName, '_blank');
  };

  return (
    <MessageDetailWrapper>
      <div className="flex flex-row items-center mb-2">
        {message.attachments.length ? <AttachmentIcon src={assets.icons.attachment} /> : null}
        <h2>
          {intl.locale === 'en' && message.subject_en !== '' ? message.subject_en : message.subject}
        </h2>
      </div>
      <HorizontalSeparatorGray className="mb-4" />
      <div className="flex flex-row items-center mb-4">
        <ClockIcon src={assets.icons.clock} />
        <span className="text-8 text-lightgray">{formatDateTime(message.time)}</span>
      </div>
      <MessageBody className="text-14 mb-4">
        {intl.locale === 'en' && message.text_en !== '' ? message.text_en : message.text}
      </MessageBody>
      {message.awaiting_acception ? (
        <div className="flex flex-row justify-end mb-8">
          {message.accepted ? (
            <span className="text-14 text-success">
              <b>
                ✓ <FormattedMessage id={'messages.details.status.accepted'} />
              </b>
            </span>
          ) : (
            <Button onClick={handleAcceptClick}>
              <FormattedMessage id={'messages.details.acceptButton'} />
            </Button>
          )}
        </div>
      ) : null}
      {message.attachments.length ? (
        <AttachmentContainer>
          {message.attachments.map((attachment: string, index: number) => (
            <div
              key={index}
              onClick={() => openAttahcment(attachment)}
              className="flex flex-row items-center mb-4 cursor-pointer"
            >
              <PdfIcon src={assets.icons.pdf_icon} />
              <span className="text-12" style={{ width: 180, marginRight: 32 }}>
                <b>{attachment}</b>
              </span>
            </div>
          ))}
        </AttachmentContainer>
      ) : null}
    </MessageDetailWrapper>
  );
};

export default MessageDetails;
